import { useState, type DragEvent } from 'react';
import type { IdsSpecification } from '@manudev/model';
import { hero } from '@/state/tour';
import { saveBytes } from '@/store/download';
import { useHeroStore } from '@/store/store';
import { REPORT_VARS, reportHtml, reportJson, type ReportMeta } from './report';
import { useRulesStore } from './state';
import { failuresOf, verdicts } from './verdicts';

export function RulesPanel() {
  const sets = useRulesStore((s) => s.sets);
  const active = useRulesStore((s) => s.active);
  const status = useRulesStore((s) => s.status);
  const error = useRulesStore((s) => s.error);
  const report = useRulesStore((s) => s.report);
  const checkedAt = useRulesStore((s) => s.checkedAt);
  const choose = useRulesStore((s) => s.choose);
  const drop = useRulesStore((s) => s.drop);
  const storeStatus = useHeroStore((s) => s.status);
  const storeError = useHeroStore((s) => s.error);
  const version = useHeroStore((s) => s.version);
  const [over, setOver] = useState(false);

  const read = async (file: File | undefined) => {
    if (file) drop(file.name, await file.text());
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    void read(e.dataTransfer.files[0]);
  };

  const label = sets.find((s) => s.id === active)?.label ?? active;
  const failing = report ? [...verdicts(report).values()].filter((v) => v === 'fail').length : 0;
  const note = !hero.ifc
    ? 'This building has no IFC to check.'
    : storeStatus === 'error'
      ? `The model failed to load: ${storeError}`
      : storeStatus !== 'ready'
        ? 'Loading the IFC…'
        : status === 'error'
          ? `The check failed: ${error}`
          : null;

  const meta = (): ReportMeta => ({
    ruleSet: label,
    model: version ? `${hero.name}, edited in the browser` : hero.name,
    checkedAt: checkedAt ?? new Date().toISOString(),
  });
  const save = (text: string, ext: string, mime: string) =>
    saveBytes(new TextEncoder().encode(text), `${hero.propsLayer}-${active}.${ext}`, mime);

  return (
    <div className="lens-panel ids">
      <div className="lens-bar">
        <span className="lens-dot" />
        <span>check against rules</span>
        <span className="lens-right">
          {status === 'checking' ? 'checking…' : report ? `${failing} elements fail` : '–'}
        </span>
      </div>
      <p className="ids-intro">
        IDS is the buildingSMART standard for information requirements.
        <br />I run the check in your browser, on the model you see, built floors included.
        <br />
        Drop your own .ids file.
      </p>
      <div className="ids-pick">
        <select value={active} onChange={(e) => choose(e.target.value)} aria-label="Rule set">
          {sets.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <label
          className="ids-drop"
          data-over={over}
          onDragOver={(e) => {
            e.preventDefault();
            setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          drop .ids or pick
          <input
            type="file"
            accept=".ids,.xml"
            onChange={(e) => {
              void read(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      {note && <p className="ids-note">{note}</p>}
      {report && (
        <div className="ids-specs">
          {report.specifications.map((spec, i) => (
            <Spec key={i} spec={spec} />
          ))}
          {report.specifications.length === 0 && <p className="ids-note">No specifications.</p>}
        </div>
      )}
      {report && (
        <div className="ids-save">
          <button
            type="button"
            onClick={() => save(reportJson(report, meta()), 'json', 'application/json')}
          >
            Report JSON
          </button>
          <button
            type="button"
            onClick={() => save(reportHtml(report, meta(), pageVars()), 'html', 'text/html')}
          >
            Report HTML
          </button>
        </div>
      )}
    </div>
  );
}

function Spec({ spec }: { spec: IdsSpecification }) {
  const requestFocus = useRulesStore((s) => s.requestFocus);
  const rows = failuresOf(spec);
  return (
    <section className="ids-spec">
      <div className="ids-spec-head">
        <span className="ids-spec-name">{spec.name}</span>
        <span className="ids-counts">
          {spec.applicable} applicable · <b data-verdict="pass">{spec.passed} pass</b> ·{' '}
          <b data-verdict="fail">{rows.length} fail</b>
        </span>
      </div>
      {rows.length > 0 && (
        <ul className="ids-fails">
          {rows.map((r) => (
            <li key={r.globalId}>
              <button type="button" onClick={() => requestFocus(r.globalId)}>
                <span className="ids-gid">{r.globalId}</span>
                <span className="ids-el">{r.name || '–'}</span>
                <span className="ids-req">{r.requirements.join(' · ')}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function pageVars(): Record<string, string> {
  const style = getComputedStyle(document.documentElement);
  return Object.fromEntries(REPORT_VARS.map((v) => [v, style.getPropertyValue(v).trim()]));
}
